import { ref } from 'vue';
import type { RouteLocationRaw } from 'vue-router';
import { defineStore } from 'pinia';

export interface DeleteBlockingItem {
  label: string;
  to: RouteLocationRaw;
}

export interface DeleteConfirmOptions {
  itemName?: string;
  message?: string;
  header?: string;
  acceptLabel?: string;
  rejectLabel?: string;
  acknowledgmentLabel?: string;
  blockedMessage?: string;
  blockingItems?: DeleteBlockingItem[];
  onConfirm: () => void | Promise<void>;
}

export const useDeleteConfirmStore = defineStore('deleteConfirm', () => {
  const visible = ref(false);
  const acknowledged = ref(false);
  const submitting = ref(false);
  const options = ref<DeleteConfirmOptions | null>(null);

  function open(opts: DeleteConfirmOptions): void {
    options.value = opts;
    acknowledged.value = false;
    submitting.value = false;
    visible.value = true;
  }

  function close(): void {
    visible.value = false;
    submitting.value = false;
    acknowledged.value = false;
    options.value = null;
  }

  async function confirm(): Promise<void> {
    if (!options.value || !acknowledged.value || submitting.value || (options.value.blockingItems?.length ?? 0) > 0) {
      return;
    }
    submitting.value = true;
    try {
      await options.value.onConfirm();
    } finally {
      close();
    }
  }

  return {
    visible,
    acknowledged,
    submitting,
    options,
    open,
    close,
    confirm,
  };
});
